import axios, {
  type AxiosError,
  type AxiosInstance,
  type InternalAxiosRequestConfig,
} from 'axios';
import type { ApiResponse } from '../types/api';
import type { AuthResponse } from '../types/user';
import { API_BASE_URL, STORAGE_ACCESS, STORAGE_REFRESH } from '../lib/constants';

export class ApiClientError extends Error {
  readonly status: number;
  readonly code: string;
  readonly fieldErrors?: Array<{ field: string; message: string }>;

  constructor(
    message: string,
    status: number,
    code: string,
    fieldErrors?: Array<{ field: string; message: string }>,
  ) {
    super(message);
    this.name = 'ApiClientError';
    this.status = status;
    this.code = code;
    this.fieldErrors = fieldErrors;
  }
}

function readAccess(): string | null {
  return localStorage.getItem(STORAGE_ACCESS);
}

function readRefresh(): string | null {
  return localStorage.getItem(STORAGE_REFRESH);
}

export function writeTokens(access: string | null, refresh: string | null): void {
  if (access) localStorage.setItem(STORAGE_ACCESS, access);
  else localStorage.removeItem(STORAGE_ACCESS);
  if (refresh) localStorage.setItem(STORAGE_REFRESH, refresh);
  else localStorage.removeItem(STORAGE_REFRESH);
}

let refreshPromise: Promise<string | null> | null = null;

async function refreshAccessToken(): Promise<string | null> {
  const rt = readRefresh();
  if (!rt) return null;
  const res = await axios.post<ApiResponse<AuthResponse>>(
    `${API_BASE_URL.replace(/\/$/, '')}/auth/refresh`,
    { refreshToken: rt },
    { headers: { 'Content-Type': 'application/json' } },
  );
  const payload = res.data;
  if (payload.error || !payload.data) return null;
  const { accessToken, refreshToken } = payload.data;
  writeTokens(accessToken, refreshToken);
  return accessToken;
}

export const api: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  headers: { 'Content-Type': 'application/json' },
});

api.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const skip = (config as InternalAxiosRequestConfig & { skipAuth?: boolean })
    .skipAuth;
  if (!skip) {
    const token = readAccess();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
  }
  return config;
});

api.interceptors.response.use(
  (res) => {
    const payload = res.data as ApiResponse<unknown>;
    if (payload && typeof payload === 'object' && 'error' in payload && payload.error) {
      const e = payload.error;
      return Promise.reject(
        new ApiClientError(
          e.message ?? 'Request failed',
          e.status,
          e.code,
          e.fieldErrors,
        ),
      );
    }
    if (payload && 'data' in payload) {
      res.data = payload.data;
    }
    return res;
  },
  async (error: AxiosError<ApiResponse<unknown>>) => {
    const original = error.config as (InternalAxiosRequestConfig & {
      skipAuth?: boolean;
      _retry?: boolean;
    }) | undefined;

    if (
      error.response?.status === 401 &&
      original &&
      !original.skipAuth &&
      !original._retry &&
      readRefresh()
    ) {
      original._retry = true;
      try {
        if (!refreshPromise) {
          refreshPromise = refreshAccessToken().finally(() => {
            refreshPromise = null;
          });
        }
        const newAccess = await refreshPromise;
        if (newAccess) {
          original.headers.Authorization = `Bearer ${newAccess}`;
          return api(original);
        }
      } catch {
        writeTokens(null, null);
      }
    }

    const body = error.response?.data;
    if (body?.error) {
      const e = body.error;
      return Promise.reject(
        new ApiClientError(
          e.message ?? error.message,
          e.status,
          e.code,
          e.fieldErrors,
        ),
      );
    }

    return Promise.reject(error);
  },
);
